import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import axios from "axios";

import {
  DEFAULT_LIST_PAGE_SIZE,
  useInfiniteListQuery,
} from "../../core/hooks";
import { api } from "../../core/services";

export const PRODUCT_PRICE_LIST_KEY = "productPriceList";

export const PRODUCT_PRICE_LIST_TABS = {
  historical: "historical",
  available: "available",
  dealFinder: "deal-finder",
};

const productPriceListUrls = {
  [PRODUCT_PRICE_LIST_TABS.historical]: (productId) =>
    `/api/products/${productId}/price-list/all`,
  [PRODUCT_PRICE_LIST_TABS.available]: (productId) =>
    `/api/products/${productId}/price-list/today`,
  [PRODUCT_PRICE_LIST_TABS.dealFinder]: (productId) =>
    `/api/products/${productId}/price-list/deal-finder`,
};

export const useProductPriceList = (
  productId,
  tab,
  filters = {},
  { enabled = true, pageSize = DEFAULT_LIST_PAGE_SIZE } = {}
) => {
  const url = productId ? productPriceListUrls[tab]?.(productId) : null;

  return useInfiniteListQuery({
    queryKey: [PRODUCT_PRICE_LIST_KEY, productId, tab, filters],
    url,
    params: {
      ...filters,
      sort: { field: "price_date", direction: "desc" },
    },
    pageSize,
    enabled: enabled && !!productId && !!url,
    staleTime: 2 * 60 * 1000,
  });
};

export const useResourcePriceAnalytics = (
  resource,
  resourceId,
  { enabled = true } = {}
) => {
  const query = useQuery({
    queryKey: ["resourcePriceAnalytics", resource, resourceId],
    queryFn: () => api.get(`/api/${resource}/${resourceId}/price-analytics`),
    enabled: enabled && !!resource && !!resourceId,
    staleTime: 1000 * 60 * 5,
  });

  return {
    analytics: query.data ?? null,
    isLoadingAnalytics: query.isLoading,
    errorAnalytics: query.error,
  };
};

export const usePricing = (productId, source, id = null) => {
  const query = useQuery({
    queryKey: ["pricing", source, productId, id],
    queryFn: async () => {
      const response = await axios.post(
        `/api/products/${productId}/price-list`,
        {
          paging: { offset: 0, limit: 50 },
          sort: { field: "price_date", direction: "desc" },
          source,
        }
      );

      return response.data.items;
    },
    enabled: !!productId,
    staleTime: 2 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
  });

  return {
    pricing: query.data || [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
};

export const usePricingAverages = (productId) => {
  const query = useQuery({
    queryKey: ["pricing-averages", productId],
    queryFn: async () => {
      const response = await axios.get(
        `/api/products/${productId}/price-averages`
      );

      return response.data;
    },
    enabled: !!productId,
    staleTime: 2 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
  });

  return {
    averages: query.data || {},
    isLoading: query.isLoading,
    isError: query.isError,
  };
};

export const usePricesByDate = (product) => {
  const query = useQuery({
    queryKey: ["pricesByDate", product?.id],
    queryFn: () => api.get(`/api/products/${product?.id}/prices-by-date`),
    staleTime: 1000 * 60 * 5,
  });

  return {
    prices: query.data || [],
    isLoadingPrices: query.isLoading,
    errorPrices: query.error,
  };
};

export const usePriceAnalytics = (product) => {
  const query = useQuery({
    queryKey: ["priceAnalytics", product?.id],
    queryFn: () => api.get(`/api/products/${product?.id}/prices/analytics`),
    staleTime: 1000 * 60 * 5,
  });

  return {
    analytics: query.data || [],
    isLoadingAnalytics: query.isLoading,
    errorAnalytics: query.error,
  };
};

export const useRefreshPricingData = (productId) => {
  const queryClient = useQueryClient();

  const refreshPricingData = () => {
    queryClient.invalidateQueries({
      queryKey: ["pricesByDate", productId],
      all: true,
    });
    queryClient.invalidateQueries({
      queryKey: ["priceAnalytics", productId],
      all: true,
    });
    // Invalidate all pricing queries for this product, regardless of source or id
    queryClient.invalidateQueries({
      predicate: (query) => {
        const [key] = query.queryKey;
        return key === "pricing" && query.queryKey.includes(productId);
      },
      refetchType: "all",
    });
    queryClient.invalidateQueries({
      queryKey: ["pricing-averages", productId],
      refetchType: "all",
    });
    queryClient.invalidateQueries({
      predicate: (query) => {
        const [key, id] = query.queryKey;
        return key === PRODUCT_PRICE_LIST_KEY && id === productId;
      },
      refetchType: "all",
    });
  };

  return refreshPricingData;
};

export const useCreateProductPrice = (productId) => {
  const refreshPricingData = useRefreshPricingData(productId);

  const mutation = useMutation({
    mutationKey: ["createProductPrice", productId],
    mutationFn: async (data) =>
      api.post(`/api/products/${productId}/prices`, data),
    onSuccess: () => refreshPricingData(productId),
  });

  return {
    createProductPrice: mutation.mutateAsync,
    status: mutation.status,
    error: mutation.error,
  };
};
